// Scope check card (Oct 30, 2026): the deal's scope numbers against the
// carrier's scope the CRM read, on the Scope card / Build approval prep panel
// above the agreement buttons (insurance, unsigned, before the build, RM tier
// and up). Boards: checking, no scope, reading, not read, couldn't read, and
// the main board (numbers, blank fields, the read's own checks, the lines left
// with the homeowner) with its all-clear look when everything matches.
//
// While finance_settings.scope_gate_mode is "on", the same check stops Submit
// for approval (trigger deals_scope_gate); this card is where the RM fixes it.

import { useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal, TeamMember } from "../../lib/types";
import {
  blankFieldsPatch,
  carrierNumbersPatch,
  dealHasNumbers,
  dealNumbers,
  dealPicks,
  describeNumberChanges,
  failedChecks,
  fetchDealForScope,
  fillableBlanks,
  fmtScopeMoney as money,
  openScopeFile,
  readRequestMessage,
  requestScopeRead,
  restoreNumbersPatch,
  scopeCachePatch,
  unreadableReason,
  uploadScopeFiles,
  type ScopeGateFile,
  type ScopeGateHook,
  type ScopeGateStatus,
  type ScopeNumberKey,
  type ScopeNumbers,
} from "../../lib/scopeGate";
import { groupPicks, pickTotals, pickedLinesText, type PickedLine } from "../../lib/scopeLines";
import ScopeLinePicker from "./ScopeLinePicker";

const STATE_TONE: Record<string, string> = {
  match: "bg-emerald-50 border-emerald-200",
  mismatch: "bg-amber-50 border-amber-300",
  flagged: "bg-slate-50 border-slate-300",
};

function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function FileLinks({ files, onError }: { files: ScopeGateFile[]; onError: (m: string) => void }) {
  if (files.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-x-2 gap-y-0.5">
      {files.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => { openScopeFile(f.storage_path).catch((e) => onError((e as Error).message)); }}
          className="text-bardin-teal hover:underline truncate max-w-[16rem]"
          title="Open the file"
        >
          {f.file_name || "scope file"}
        </button>
      ))}
    </span>
  );
}

/** The lines left with the homeowner, grouped, no more than a few per group (staff screens). */
export function PickedLinesList({ picks, limit = 6 }: { picks: PickedLine[]; limit?: number }) {
  const groups = groupPicks(picks);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {g.title} · ACV {money(g.acv)}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {g.items.slice(0, limit).map((p, i) => (
              <li key={i} className="flex items-baseline justify-between gap-2 text-xs text-slate-700">
                <span className="min-w-0 truncate">{p.n ? `#${p.n} ` : ""}{p.description}{p.area ? <span className="text-slate-400"> · {p.area}</span> : null}</span>
                <span className="shrink-0 text-slate-600">{money(p.acv)}</span>
              </li>
            ))}
            {g.items.length > limit && <li className="text-[11px] text-slate-400">+{g.items.length - limit} more</li>}
          </ul>
        </div>
      ))}
    </div>
  );
}

function UploadButton({ label, busy, onFiles }: { label: string; busy: boolean; onFiles: (files: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="px-3 py-1.5 text-xs font-semibold rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
      >
        {busy ? "Uploading…" : label}
      </button>
      <input
        ref={ref}
        type="file"
        multiple
        accept=".pdf,image/*,.heic,.heif"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length > 0) onFiles(files);
        }}
      />
    </>
  );
}

export default function ScopeCheckCard({
  deal,
  scope,
  member,
  canEdit,
  onReload,
}: {
  deal: Deal;
  scope: ScopeGateHook;
  member: TeamMember | null;
  canEdit: boolean;
  onReload: () => void | Promise<void>;
}) {
  const { status, error, refresh } = scope;
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The numbers "Use the carrier's numbers" replaced, on the deal it changed. Undo is offered only on that deal,
  // and only while it still has the carrier's numbers (an edit since then ends it).
  const [undo, setUndo] = useState<{ dealId: string; prev: ScopeNumbers; after: ScopeNumbers; label: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  async function note(content: string, metadata: Record<string, unknown>) {
    await supabase.from("deal_notes").insert({ deal_id: deal.id, author_id: member?.id ?? null, note_type: "agreement", content, metadata });
  }

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setErr(null);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setErr((e as { message?: string })?.message ?? "Something went wrong.");
    } finally {
      setBusy(null);
    }
  }

  async function afterWrite() {
    await Promise.all([refresh(), Promise.resolve(onReload())]);
  }

  if (!status) {
    return (
      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        {error ? (
          <span className="flex items-center justify-between gap-2">
            <span className="text-rose-700">Couldn't check the carrier's scope: {error}</span>
            <button type="button" onClick={() => void refresh()} className="font-medium text-bardin-teal hover:underline">Try again</button>
          </span>
        ) : (
          "Checking the carrier's scope…"
        )}
      </div>
    );
  }

  const s: ScopeGateStatus = status;
  const blockingNote = s.applies && s.blocks
    ? s.can_override
      ? "Submit for approval is blocked until this is fixed, or Alex or Chad override."
      : "Submit for approval is blocked until this is fixed."
    : null;
  const notBlocking = !s.applies ? <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">check only · not blocking yet</span> : null;

  function uploadFiles(files: File[]) {
    void run("upload", async () => {
      const r = await uploadScopeFiles(deal.id, files, member?.id ?? null);
      if (r.failed.length > 0) setErr(r.failed.map((f) => `${f.name}: ${f.error}`).join(" · "));
      if (r.uploaded.length > 0) setMsg(`Uploaded ${r.uploaded.length === 1 ? r.uploaded[0] : `${r.uploaded.length} files`}. The CRM reads it in a minute or two.`);
      await afterWrite();
    });
  }

  function readNow() {
    void run("read", async () => {
      const r = await requestScopeRead(deal.id);
      setMsg(readRequestMessage(r));
      await refresh();
    });
  }

  const header = (title: string, icon: string) => (
    <div className="flex items-start justify-between gap-2">
      <div className="text-xs font-semibold text-bardin-ink flex items-center gap-1.5 flex-wrap">
        <span>{icon}</span>
        <span>{title}</span>
        {notBlocking}
      </div>
    </div>
  );

  const footer = (
    <>
      {msg && <div className="mt-2 text-[11px] text-emerald-800">{msg}</div>}
      {err && <div className="mt-2 rounded bg-rose-50 border border-rose-200 px-2 py-1 text-[11px] text-rose-800">{err}</div>}
      {blockingNote && <div className="mt-2 text-[11px] font-medium text-rose-700">{blockingNote}</div>}
    </>
  );

  // ── No carrier scope on file ──────────────────────────────────────────────
  if (s.state === "no_scope") {
    return (
      <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
        {header("No carrier scope on this deal", "📄")}
        <div className="mt-1 text-[11px] text-amber-900">
          Upload the carrier's scope: the PDF, or photos of every page including the summary. The CRM reads it and checks this deal's numbers against it.
        </div>
        {canEdit && (
          <div className="mt-2">
            <UploadButton label="Upload the carrier's scope" busy={busy === "upload"} onFiles={uploadFiles} />
          </div>
        )}
        {footer}
      </div>
    );
  }

  // ── Being read ──────────────────────────────────────────────────────────────
  if (s.state === "reading") {
    return (
      <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 p-3">
        {header("Reading the carrier's scope…", "⏳")}
        <div className="mt-1 text-[11px] text-sky-900 flex flex-wrap gap-x-1">
          <span>Usually a minute or two.</span>
          <FileLinks files={s.files} onError={setErr} />
        </div>
        {s.applies && <div className="mt-1 text-[11px] text-sky-800">Submit opens when the read is done.</div>}
        {msg && <div className="mt-2 text-[11px] text-emerald-800">{msg}</div>}
        {err && <div className="mt-2 rounded bg-rose-50 border border-rose-200 px-2 py-1 text-[11px] text-rose-800">{err}</div>}
      </div>
    );
  }

  // ── On file, never read ───────────────────────────────────────────────────
  if (s.state === "not_read") {
    return (
      <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
        {header("The carrier's scope hasn't been read yet", "📄")}
        <div className="mt-1 text-[11px] text-amber-900 flex flex-wrap gap-x-1">
          <span>On file:</span>
          <FileLinks files={s.files} onError={setErr} />
        </div>
        {canEdit && (
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={readNow}
              disabled={busy !== null}
              className="px-3 py-1.5 text-xs font-semibold rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === "read" ? "Asking…" : "Read it now"}
            </button>
            <UploadButton label="Upload a new scope" busy={busy === "upload"} onFiles={uploadFiles} />
          </div>
        )}
        {footer}
      </div>
    );
  }

  // ── Couldn't use the read ─────────────────────────────────────────────────
  if (s.state === "unreadable") {
    return (
      <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
        {header("The CRM couldn't read the carrier's scope", "⚠")}
        <div className="mt-1 text-[11px] text-rose-900">{unreadableReason(s.read)}</div>
        <div className="mt-0.5 text-[11px] text-rose-900 flex flex-wrap gap-x-1">
          <span>File:</span>
          <FileLinks files={s.files} onError={setErr} />
        </div>
        {canEdit && (
          <div className="mt-2 flex flex-wrap gap-2">
            <UploadButton label="Upload the full scope" busy={busy === "upload"} onFiles={uploadFiles} />
            {s.read?.status === "failed" && (
              <button
                type="button"
                onClick={readNow}
                disabled={busy !== null}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busy === "read" ? "Asking…" : "Read it again"}
              </button>
            )}
          </div>
        )}
        {footer}
      </div>
    );
  }

  // ── A usable read: match / mismatch / flagged ──────────────────────────────
  const picks = dealPicks(deal);
  const totals = pickTotals(picks);
  const numbers = carrierNumbersPatch(s.fields);
  const canUseNumbers = canEdit && Object.keys(numbers).length > 0;
  const fills = fillableBlanks(deal, s.blanks);
  const failed = failedChecks(s.read);
  const lineCount = s.read?.line_count ?? 0;
  const credits = s.credits;
  const allClear = s.state === "match" && (!credits || credits.ok) && fills.length === 0;
  const title =
    s.state === "match"
      ? credits && !credits.ok ? "The numbers match; the credits need a look" : "Matches the carrier's scope"
      : s.state === "mismatch"
        ? "Doesn't match the carrier's scope"
        : "The read of the carrier's scope needs a person's look";

  function applyCarrierNumbers() {
    const dealId = deal.id;
    const after = numbers;
    void run("numbers", async () => {
      // The caches come from the deal as it is now, not the page's copy of it.
      const current = await fetchDealForScope(dealId);
      const prev = dealNumbers(current, Object.keys(after) as ScopeNumberKey[]);
      const patch: Record<string, unknown> = { ...after, ...scopeCachePatch({ ...current, ...after }) };
      const { error: upErr } = await supabase.from("deals").update(patch).eq("id", dealId);
      if (upErr) throw upErr;
      const changes = describeNumberChanges(s.fields);
      await note(`Used the carrier's numbers from the scope: ${changes}.`, {
        action: "scope_use_carrier_numbers", read_id: s.read?.id ?? null, before: prev, after,
      });
      setUndo({ dealId, prev, after, label: changes });
      setMsg("Used the carrier's numbers.");
      await afterWrite();
    });
  }

  function undoNumbers() {
    if (!undo || undo.dealId !== deal.id) return;
    const u = undo;
    void run("undo", async () => {
      const current = await fetchDealForScope(u.dealId);
      if (!dealHasNumbers(current, u.after)) {
        setUndo(null);
        throw new Error("The numbers changed after you used the carrier's, so there's nothing to undo.");
      }
      // Only the four numbers go back; the caches follow the deal as it is now (upgrades or credits saved since stay).
      const { error: upErr } = await supabase.from("deals").update(restoreNumbersPatch(current, u.prev)).eq("id", u.dealId);
      if (upErr) throw upErr;
      await note(`Put back the numbers from before using the carrier's (${u.label}).`, { action: "scope_undo_carrier_numbers", restored: u.prev });
      setUndo(null);
      setMsg("Put the earlier numbers back.");
      await afterWrite();
    });
  }
  const canUndo = !!undo && undo.dealId === deal.id && dealHasNumbers(deal, undo.after);

  function fillBlanks() {
    void run("blanks", async () => {
      const patch = blankFieldsPatch(fills);
      const { error: upErr } = await supabase.from("deals").update(patch).eq("id", deal.id);
      if (upErr) throw upErr;
      await note(`Filled from the carrier's scope: ${fills.map((f) => `${f.label} ${f.value}`).join(", ")}.`, {
        action: "scope_fill_blanks", read_id: s.read?.id ?? null, filled: patch,
      });
      setMsg(`Filled ${fills.length} field${fills.length === 1 ? "" : "s"} from the scope.`);
      await afterWrite();
    });
  }

  async function savePicks(list: PickedLine[]) {
    const dealId = deal.id;
    const t = pickTotals(list);
    const current = await fetchDealForScope(dealId);
    const patch: Record<string, unknown> = {
      items_not_performing_detail: list.length > 0 ? list : null,
      acv_credit: t.acv,
      dep_credit: t.dep,
      items_not_performing_lines: pickedLinesText(list),
      ...scopeCachePatch({ ...current, acv_credit: t.acv, dep_credit: t.dep }),
    };
    const { error: upErr } = await supabase.from("deals").update(patch).eq("id", dealId);
    if (upErr) throw upErr;
    await note(
      list.length > 0
        ? `Left ${list.length} line${list.length === 1 ? "" : "s"} of the carrier's scope with the homeowner (${pickedLinesText(list)}): ACV ${money(t.acv)} credited to the deductible, depreciation ${money(t.dep)} stays with the carrier.`
        : "Cleared the lines left with the homeowner: no credit for work Bardin won't do.",
      { action: "scope_pick_lines", read_id: s.read?.id ?? null, lines: list.length, acv_credit: t.acv, dep_credit: t.dep },
    );
    setMsg(list.length > 0 ? "Saved the lines left with the homeowner." : "Cleared the lines left with the homeowner.");
    await afterWrite();
  }

  const creditsMessage =
    credits && !credits.ok
      ? credits.state === "typed"
        ? `The credits on this deal were typed in (ACV ${money(credits.deal_acv)}, depreciation ${money(credits.deal_dep)}). Pick the lines Bardin won't do instead.`
        : credits.state === "stale"
          ? `${credits.stale.length === 1 ? "1 line" : `${credits.stale.length} lines`} left with the homeowner ${credits.stale.length === 1 ? "isn't" : "aren't"} on the carrier's current scope. Pick the lines again.`
          : credits.state === "mismatch"
            ? "The credits on this deal don't add up to the lines picked. Save the picks again."
            : credits.state === "no_lines"
              ? `The credits on this deal (ACV ${money(credits.deal_acv)}, depreciation ${money(credits.deal_dep)}) can't be checked: the carrier's scope has no line items. Ask Alex or Chad to override when you submit.`
              : null
      : null;

  return (
    <div className={`mb-3 rounded-lg border p-3 ${allClear ? STATE_TONE.match : s.state === "match" ? STATE_TONE.mismatch : STATE_TONE[s.state] ?? "bg-slate-50 border-slate-200"}`}>
      {header(title, allClear ? "✓" : s.state === "flagged" ? "🔎" : "⚠")}
      <div className="mt-0.5 text-[11px] text-slate-600 flex flex-wrap gap-x-1">
        <span>Read from</span>
        <FileLinks files={s.files} onError={setErr} />
        {s.read?.carrier && <span>· {s.read.carrier}</span>}
        {s.read?.finished_at && <span>· {ago(s.read.finished_at)}</span>}
      </div>
      {s.newer_unusable && (
        <div className="mt-1 text-[11px] text-amber-800">
          A newer upload ({s.newer_unusable.files.join(", ") || "a file"}) {s.newer_unusable.status === "queued" || s.newer_unusable.status === "running" ? "is still being read" : "couldn't be read"}; this is the last good read.
        </div>
      )}

      {/* The four numbers (folded once everything matches) */}
      <details className="mt-2" open={!allClear}>
        <summary className="cursor-pointer select-none text-[11px] text-slate-600">
          {s.mismatches > 0
            ? `${s.mismatches} of ${s.fields.length} numbers don't match`
            : s.unknown > 0
              ? `${s.unknown} number${s.unknown === 1 ? "" : "s"} not on the scope`
              : `All ${s.fields.length} numbers match`}
        </summary>
      <div className="mt-1 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-2 py-1 text-left font-semibold">Number</th>
              <th className="px-2 py-1 text-right font-semibold">This deal</th>
              <th className="px-2 py-1 text-right font-semibold">Carrier's scope</th>
              <th className="px-1 py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {s.fields.map((f) => (
              <tr key={f.key} className={`border-t border-slate-100 ${f.match === false ? "bg-amber-50/60" : ""}`}>
                <td className="px-2 py-1 text-slate-700">{f.label}</td>
                <td className={`px-2 py-1 text-right ${f.match === false ? "font-semibold text-amber-900" : "text-slate-700"}`}>
                  {f.key === "pwi_code" && !f.deal ? "none" : money(f.deal)}
                </td>
                <td className="px-2 py-1 text-right text-slate-700">
                  {f.key === "pwi_code" && !f.carrier ? "none" : f.carrier == null ? "not on the scope" : money(f.carrier)}
                </td>
                <td className="px-1 py-1 text-center" title={f.match === false ? "Doesn't match" : f.match ? "Matches" : "The read has no number"}>
                  {f.match === false ? <span className="text-amber-700 font-bold">≠</span> : f.match ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">?</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </details>

      {(canUseNumbers || canUndo) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {canUseNumbers && (
            <button
              type="button"
              onClick={applyCarrierNumbers}
              disabled={busy !== null}
              className="px-3 py-1.5 text-xs font-semibold rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {busy === "numbers" ? "Saving…" : "Use the carrier's numbers"}
            </button>
          )}
          {canUndo && !canUseNumbers && (
            <button
              type="button"
              onClick={undoNumbers}
              disabled={busy !== null}
              className="px-3 py-1.5 text-xs font-medium rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === "undo" ? "Putting back…" : "Undo"}
            </button>
          )}
          {s.state === "flagged" && canUseNumbers && (
            <span className="text-[11px] text-slate-600">The read didn't pass its own checks: compare with the scope first.</span>
          )}
        </div>
      )}

      {/* Blank fields the scope can fill */}
      {canEdit && fills.length > 0 && (
        <div className="mt-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700">
          <div>
            The scope has {fills.length === 1 ? "a field" : `${fills.length} fields`} this deal is missing:{" "}
            {fills.map((f) => `${f.label} (${f.value})`).join(", ")}.
          </div>
          <button
            type="button"
            onClick={fillBlanks}
            disabled={busy !== null}
            className="mt-1 px-2.5 py-1 text-[11px] font-semibold rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy === "blanks" ? "Filling…" : `Fill the ${fills.length === 1 ? "blank field" : `${fills.length} blank fields`}`}
          </button>
        </div>
      )}

      {/* The read's own checks */}
      {(s.read?.checks?.length ?? 0) > 0 && (
        <details className="mt-2 text-[11px]" open={failed.length > 0}>
          <summary className="cursor-pointer select-none text-slate-600">
            {failed.length === 0 ? "✓ The read checked its own math" : `⚠ The read's own checks: ${failed.length} didn't pass`}
          </summary>
          <ul className="mt-1 space-y-0.5 pl-1">
            {(s.read?.checks ?? []).map((c, i) => (
              <li key={i} className={c.ok ? "text-slate-500" : "text-amber-800"}>
                {c.ok ? "✓" : "⚠"} {c.detail}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* What can stay with the homeowner */}
      <div className="mt-2 rounded border border-slate-200 bg-white px-2 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 text-[11px] text-slate-700">
            <div className="font-semibold text-bardin-ink">What can stay with the homeowner</div>
            {picks.length > 0 ? (
              <div>
                {picks.length} line{picks.length === 1 ? "" : "s"} · ACV {money(totals.acv)} off the deductible · depreciation {money(totals.dep)} stays with the carrier
              </div>
            ) : lineCount > 0 ? (
              <div className="text-slate-500">Nothing picked: Bardin does every line of the scope.</div>
            ) : (
              <div className="text-slate-500">The scope has no line items to pick from.</div>
            )}
          </div>
          {canEdit && lineCount > 0 && s.read?.id && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={busy !== null}
              className="shrink-0 px-2.5 py-1 text-[11px] font-semibold rounded bg-amber-100 text-amber-900 hover:bg-amber-200 disabled:opacity-50"
            >
              {picks.length > 0 ? "Change" : "Pick lines"}
            </button>
          )}
        </div>
        {picks.length > 0 && (
          <div className="mt-1.5 border-t border-slate-100 pt-1.5">
            <PickedLinesList picks={picks} limit={4} />
          </div>
        )}
        {creditsMessage && <div className="mt-1.5 text-[11px] font-medium text-amber-800">{creditsMessage}</div>}
      </div>

      {footer}

      {pickerOpen && s.read?.id && (
        <ScopeLinePicker
          deal={deal}
          readId={s.read.id}
          initialPicks={picks}
          onClose={() => setPickerOpen(false)}
          onSave={savePicks}
        />
      )}
    </div>
  );
}

/**
 * The submit gate (Oct 30, 2026): shown in place of Submit for approval (and
 * Resubmit) while the scope check blocks. With can_override the RM sends the
 * submission anyway, as a request Alex or Chad answer at approval.
 */
export function ScopeSubmitBlock({
  status,
  busy,
  verb = "submit",
  fixHint = "Fix it on the scope check above.",
  onRequest,
}: {
  status: ScopeGateStatus;
  busy: boolean;
  verb?: "submit" | "resubmit";
  /** Where the RM fixes it; the review window points back at the deal page. */
  fixHint?: string;
  onRequest: (note: string) => void | Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  const ok = note.trim().length >= 5;
  return (
    <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
      <div className="text-xs font-semibold text-rose-900">Can't {verb} yet</div>
      <div className="mt-0.5 text-xs text-rose-800">
        {status.message ?? "This deal doesn't match the carrier's scope."} {fixHint}
      </div>
      {status.can_override &&
        (!asking ? (
          <button
            type="button"
            onClick={() => setAsking(true)}
            disabled={busy}
            className="mt-2 px-3 py-1.5 text-xs font-semibold rounded bg-white border border-rose-300 text-rose-800 hover:bg-rose-100 disabled:opacity-50"
          >
            Ask Alex or Chad to override
          </button>
        ) : (
          <div className="mt-2 space-y-1.5">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              autoFocus
              placeholder="Why this deal's numbers are right (Alex or Chad read this)"
              className="w-full text-xs p-2 border border-rose-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-rose-300"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setAsking(false); setNote(""); }}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onRequest(note.trim())}
                disabled={busy || !ok}
                title={ok ? undefined : "Say why in a few words"}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send the request"}
              </button>
            </div>
            <div className="text-[11px] text-rose-700">
              It goes to Alex and Chad as an approval that needs an override. They approve it with a reason, use the carrier's numbers, or send it back.
            </div>
          </div>
        ))}
    </div>
  );
}
